/*
Make calculations for data visualisation based on survey answers
We need to base on the answers saved in API - and to have overall progress 100%

Algorithm description https://wppcloud-my.sharepoint.com/:w:/g/personal/rafal_potempa_wundermanthompson_com/EYM71-1-6MFGrrqOaY6hSokBx-rsSyDFlPZSX2OvIPcGbA?e=4%3A95cRJG&at=9
*/
import random from "pex-random";
import * as Survey from "survey-react";
import { surveyModel } from "../mock/survey-questions.js";
import {
  getAnsweredPages,
  getConsumerLevel,
  getMergedQuestionsAndAnswers,
} from "../utils";

import MacallanAPI from "../api/macallan.js";
import hash from "object-hash";

//const CARDINALITIES = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
const CARDINALITIES = ["E", "NE", "N", "NW", "W", "SW", "S", "SE"];

export const NOVICE = 0;
export const CONNOISSEUR = 1;
export const EXPERT = 2;

// Arrays for frequency, octaves and amplitued - used for bark noise calculations
const FREQUENCY = [
  [0.77, 0.82],
  [0.83, 0.88],
  [0.89, 0.94],
  [0.95, 1.4],
  [1.41, 1.84],
];
const OCTAVES = [4, 4, 5, 5, 6];
const AMPITUDE = [
  [0.55, 0.63],
  [0.64, 0.72],
  [0.73, 0.82],
  [0.83, 0.92],
  [0.93, 1.04],
];

const COLORS = ["#f2e8d5", "#ac9d81", "#b99578"];

// Cardinality global level levels - stored the low and high values for each cardinality, to choose form mock/characteristics.js
// If less or equal "low" - use "low"
// if greater or equal "high" - use "high"
// in other case use "med"
// THOSE VALUES SHOULD BE REVISED - perhaps they don't make much sense and there won't be an option to have "high"...
const CARDINALITY_LEVELS = [
  [3, 7],
  [3, 7],
  [2, 5],
  [1, 3],
  [4, 9],
  [2, 5],
  [2, 5],
  [3, 7],
];

// Object of available textures based on catalogue structure (file names have to be updated manually)
const TEXTURES = {
  wood: {
    0: [
      "texture1.jpg",
      "texture2.jpg",
      "texture3.jpg",
      "texture4.jpg",
      "texture5.jpg",
      "texture6.jpg",
    ],
    1: [
      "texture1.jpg",
      "texture2.jpg",
      "texture3.jpg",
      "texture4.jpg",
      "texture5.jpg",
      "texture6.jpg",
    ],
    2: [
      "texture1.jpg",
      "texture2.jpg",
      "texture3.jpg",
      "texture4.jpg",
      "texture5.jpg",
      "texture6.jpg",
    ],
  },
  bark: {
    0: ["bark1.jpg"],
    1: ["bark1.jpg"],
    2: ["bark1.jpg"],
  },
};

// Calculate the number of questions answered, based on API - it is the easiest to use SurveyJS to have all the visible pages (which should be all the user answers)

const calcVisualisationData = async function (
  context,
  answers,
  tempParams = {}
) {
  // Cardinality levels
  const CL_Low = tempParams.CL_Low ?? 0.3;
  const CL_Medium = tempParams.CL_Medium ?? 0.5;
  const CL_High = tempParams.CL_High ?? 0.7;
  const CL_Double = tempParams.CL_Double ?? 0.7;

  // Cardinality weight
  const N_Weight = tempParams.N_Weight ?? 0.3;
  const NE_Weight = tempParams.NE_Weight ?? 0.18;
  const E_Weight = tempParams.E_Weight ?? 0.3;
  const SE_Weight = tempParams.SE_Weight ?? 0.2;
  const S_Weight = tempParams.S_Weight ?? 0.3;
  const SW_Weight = tempParams.SW_Weight ?? 0.25;
  const W_Weight = tempParams.W_Weight ?? 0.3;
  const NW_Weight = tempParams.NW_Weight ?? 0.15;
  /*
  Survey.Serializer.addProperty("page", "chapterName:text");
  Survey.Serializer.addProperty("page", "chapter:number");
  Survey.Serializer.addProperty("page", "isEasterEgg:boolean");
  Survey.Serializer.addProperty("page", "isAnswered:boolean");
  Survey.Serializer.addProperty("page", "isMarkedForLater:boolean");
  Survey.Serializer.addProperty("page", "background:text");
  Survey.Serializer.addProperty("page", "backgroundOpacity:number");
  Survey.Serializer.addProperty("page", "preloadBackground:array");
  Survey.Serializer.addProperty("question", "customCssClass:text");
  Survey.Serializer.addProperty("page", "crmId:text");
  Survey.Serializer.addProperty("question", "crmId:array");
  Survey.Serializer.addProperty("page", "disabledMarkForLater:boolean");
  Survey.Serializer.addProperty("page", "cardinalities:array");
*/
  // get all questions from defined json
  const surveyMTC = new Survey.Model(surveyModel());
  //console.log(answers);

  const api = new MacallanAPI(context.accessToken);

  /*
    store values in the API config object:
    {
      id: String, (get from API)
      answeredQuestionsCount: Number,
      consumerLevel: Number,
      thickness: Number[],
      axisNumbers: Number[],
      bark: { frequency: Number, octaves: Number, amplitude: Number, count: Number},
      color: String,
      barkTexture: String,
      woodTexture: String
    }
  */
  let storedData = null;
  let thickness = [];
  let consumerId = "";
  let dataChanged = false;

  // check if we have random things stored in user profile already. And if question count and consumer level has been changed - need to do reset the randomness
  await api
    .getConfig()
    .then((data) => {
      consumerId = data.consumerId;
      storedData = JSON.parse(data.config);
      context.setConsumerId(consumerId);
      context.setStoredData(storedData);
    })
    .catch((e) => console.log(e))
    .finally(() => {
      if (api.accessToken !== context.accessToken)
        context.setAccessToken(api.accessToken);
    });

  // Structure to be passed into visualisation comonent
  const data = {
    id: consumerId,
    location: "north-america", //4 options
    color: "", //3 colors from COLORS, based on consumer level
    woodTexture: "", // texture from TEXTURES.wood, based on consumer level
    barkTexture: "", // texture from TEXTURES.bark, based on consumer level
    dirtTexture: "/textures/dirt/texture1.jpg", // static texture placeholder
    barkDirtTexture: "/textures/barkDirt/texture1.jpg", // static texture placeholder
    rings: [], // {radius: float[], thickness: int}
    bark: {
      frequency: random.float(0, 2),
      octaves: random.int(1, 7),
      amplitude: random.float(0.2, 1.5),
    },
  };

  if (answers) {
    const pages = surveyMTC.pages;

    // find each question from "answers" and select the values
    getMergedQuestionsAndAnswers(pages, answers, surveyMTC);

    // In the API there might be more answers than we should show (because of changes in conditional yes/no questions)
    // get all answered and visible pages per SurveyJS
    const answeredPages = getAnsweredPages(surveyMTC);

    const answeredQuestionsCount = answeredPages.length;
    if (answeredQuestionsCount) {
      // can be NOVICE, CONNOISSEUR, EXPERT
      let levels = {
        novice: NOVICE,
        connoisseur: CONNOISSEUR,
        expert: EXPERT,
      };
      const consumerLevel = getConsumerLevel(answeredPages, answers, levels);
      if (!!storedData && storedData?.consumerLevel === consumerLevel) {
        data.color = storedData.color;
        data.woodTexture = storedData.woodTexture;
        data.barkTexture = storedData.barkTexture;

        // assign thickness - make some random choices based on consumer level and questions count or get Array from store
        if (storedData?.answeredQuestionsCount === answeredQuestionsCount) {
          thickness = [...storedData.thickness];
        } else {
          thickness = ringThickness(consumerLevel, answeredQuestionsCount);
          dataChanged = true;
        }
      } else {
        data.color = COLORS[consumerLevel];
        data.woodTexture = getTexturePath(consumerLevel, "wood");
        data.barkTexture = getTexturePath(consumerLevel, "bark");
        thickness = ringThickness(consumerLevel, answeredQuestionsCount);
        dataChanged = true;
      }

      // some random choices for the bark
      const randomBark = barkNoise(
        answeredPages,
        answers,
        storedData?.bark?.count
      );
      // get stored data if barkCount did not changed
      data.bark = randomBark.changed ? randomBark : storedData.bark;
      delete data.bark.changed;

      // It’s an array with randomized axis numbers that will allow to introduce some gaps into the chart - get stored data if answered question count did not changed
      const axisNumbers =
        storedData?.answeredQuestionsCount !== answeredQuestionsCount
          ? getAxisNumbers(answeredQuestionsCount)
          : storedData.axisNumbers;

      const cardinalityLevels = [CL_Low, CL_Medium, CL_High, CL_Double];

      // const CARDINALITIES = ["E", "NE", "N", "NW", "W", "SW", "S", "SE"];
      const cardinalityWeights = [
        E_Weight,
        NE_Weight,
        N_Weight,
        NW_Weight,
        W_Weight,
        SW_Weight,
        S_Weight,
        SE_Weight,
      ];
      /*
      const cardinalityWeights = [
        N_Weight,
        NE_Weight,
        E_Weight,
        SE_Weight,
        S_Weight,
        SW_Weight,
        W_Weight,
        NW_Weight,
      ];
      */

      const currentOffset = [0, 0, 0, 0, 0, 0, 0, 0];

      //  array of cardinalities in answers count
      const cardinalitiesCount = [0, 0, 0, 0, 0, 0, 0, 0]; // CL_Double should count as 2?
      const tempAnswerCardinalities = {};

      // intersections variables
      const prevValues = [];
      const radiusOffset = [
        [1, 1],
        [1, 1],
        [1, 1],
        [1, 1],
        [1, 1],
        [1, 1],
        [1, 1],
        [1, 1],
      ];
      const baseOffset = 1;

      const answersCurrent = [];
      answeredPages.forEach((page, answerIndex) => {
        // get current answer
        const answer = answers[page.crmId];
        if (typeof answer === undefined) return;

        answersCurrent.push(answer);

        let answerCardinalities = {};

        // is survey-questions.js there is "cardinalities" definition for the questions
        if (page.cardinalities && answer !== null) {
          // take into account special type "selection" - to calculate number of answers (or whiskies selected)
          if (page.cardinalities.selection) {
            for (let i = 0; i < page.cardinalities.selection.length; i++) {
              if (answer?.length >= page.cardinalities.selection[i].range) {
                answerCardinalities =
                  page.cardinalities.selection[i].cardinalities;
                break;
              }
            }
          } else if (typeof answer === "object") {
            // there are multiple answers and for each there could be some cardinality
            answer.forEach((a) => {
              const c = page.cardinalities[a.toString()];
              // if cardinality already exists - make it "double" - index=3
              if (c) {
                Object.keys(c).forEach((key) => {
                  if (key in answerCardinalities) {
                    c[key] = 3;
                  }
                });
                answerCardinalities = { ...answerCardinalities, ...c };
              }
            });
          } else {
            const answer2 =
              typeof answer === "string" ? answer.split("|")[0] : answer;
            answerCardinalities = page.cardinalities[answer2.toString()] || {};
          }
        }

        if (answerCardinalities) {
          // add offset for each cardinality that is assigned to the answer
          CARDINALITIES.forEach((c, i) => {
            if (c in answerCardinalities) {
              currentOffset[i] = currentOffset[i] + 3;
            }
          });
        }

        //Scn = Wc * (An + (Lcn * Ocn))
        /*
        c – cardinality 
        n – question number  

        Ocn – offset value for particular cardinality and question number 
        Vc – cardinality value – 1 if answer is marked as a particular cardinality specific, 0 if not 

        Scn – score for particular question for particular cardinality 
        Wc – cardinality weight, 
        An – axis number  
        Lcn – cardinality level for particular answer  
        */

        // calculate radius for each cardinality
        const ring = [0, 0, 0, 0, 0, 0, 0, 0];

        CARDINALITIES.forEach((c, i) => {
          let cardinalityLevel = 0;
          const minOffset = Math.min(
            radiusOffset[(i, 0)],
            radiusOffset[(i, 1)]
          );

          if (answerCardinalities && c in answerCardinalities) {
            cardinalityLevel = cardinalityLevels[answerCardinalities[c]];
            cardinalitiesCount[i]++;
            if (answerCardinalities[c] === 3) cardinalitiesCount[i]++; // for CL_Double
          } else {
            cardinalityLevel = cardinalityLevels[1];
          }
          let value =
            cardinalityWeights[i] *
            (axisNumbers[answerIndex] + cardinalityLevel * currentOffset[i]);

          // console.log({
          //   answerIndex: answerIndex,
          //   prevValue: prevValues[i],
          //   value: value,
          //   RO_A: radiusOffset[i,0],
          //   RO_B: radiusOffset[i,1],
          //   minOffset: minOffset,
          //   i: i
          // });

          /*
            preventing intersections
          */
          if (
            answerIndex > 0 &&
            !!prevValues[i] &&
            value < prevValues[i] + baseOffset
          ) {
            value = prevValues[i] + minOffset;
          }

          const rOffset = !!prevValues[i] ? value - prevValues[i] : baseOffset;

          prevValues[i] = Math.round((value + Number.EPSILON) * 100) / 100;
          radiusOffset[(i, answerIndex % 2)] =
            Math.round((rOffset + Number.EPSILON) * 100) / 100;

          ring[i] = Math.round((value + Number.EPSILON) * 100) / 100;
        });
        tempAnswerCardinalities[page.name] = answerCardinalities;
        data.rings.push({
          radius: ring,
          thickness: thickness[answerIndex],
        });
      });
      // console.log("cc", cardinalitiesCount);
      const topCardinalitites = getTopCardinalities(cardinalitiesCount);

      // data changed also when cardinalities count is different
      if (
        !dataChanged &&
        (!storedData?.cardinalitiesCount ||
          JSON.stringify(storedData?.cardinalitiesCount) !==
            JSON.stringify(cardinalitiesCount))
      ) {
        dataChanged = true;
      }

      // in the end... compare all the answers
      const answersHash = hash(answersCurrent);
      const oldAnswersHash = storedData?.answersHash;
      if (!dataChanged && answersHash !== oldAnswersHash) {
        dataChanged = true;
      }

      data.calculations = {
        CARDINALITIES,
        topCardinalities: topCardinalitites,
        axisNumbers,
        cardinalitiesCount,
        answerCardinalities: tempAnswerCardinalities,
      };

      const {
        location,
        rings,
        calculations,
        dirtTexture,
        barkDirtTexture,
        ...tempData
      } = data;

      storedData = {
        ...storedData,
        consumerLevel,
        answeredQuestionsCount,
        thickness: [...thickness],
        axisNumbers: [...data.calculations.axisNumbers],
        cardinalitiesCount,
        answersHash,
        ...tempData,
      };

      if (!storedData?.date || dataChanged) {
        storedData.date = new Date();
      }
    }
  }

  api.setConfig(storedData);
  context.setStoredData(storedData);
  return { data, dataChanged, date: storedData.date };
};

/*
Based on questions
C3Q1, C3Q6, C3Q16, C3Q19
Return: frequency, octaves and amplitude
*/
function barkNoise(pages, answers, storedBarkCount = null) {
  // find questions C3Q1, C3Q6, C3Q16, C3Q19 in answered and if YES is answered, calculate as "bark count"
  let barkCount = 0;
  const pageNames = ["q_3_1", "q_3_6", "q_3_16", "q_3_19"];

  pages.forEach((page, answerIndex) => {
    if (pageNames.includes(page.name) && answers[page.crmId]) {
      if (
        answers[page.crmId] === true ||
        (page.name === "q_3_6" &&
          ["investment", "gift", "job"].includes(answers[page.crmId]))
      ) {
        barkCount++;
      }
    }
  });

  if (barkCount === storedBarkCount) {
    return {
      changed: false,
    };
  }

  return {
    changed: true,
    frequency: random.float(FREQUENCY[barkCount][0], FREQUENCY[barkCount][1]),
    octaves: OCTAVES[barkCount],
    amplitude: random.float(AMPITUDE[barkCount][0], AMPITUDE[barkCount][1]),
    count: barkCount,
  };
}

/*
Consumer level:
Novice: 10% thicker rings in the center, with 90% thin rings 
Connoisseur:  30% thicker rings in the center, with 70% thin rings 
Expert:  50% thicker in the center, with 50% thin rings 
Thin ring: 1-4, Thick ring: 6-10
Return: array with thickness level for each question
*/
function ringThickness(level, questionsCount) {
  const centerEnd = Math.floor(questionsCount / 2);

  const thickness = [];
  let drawPool = [];
  let drawThick = []; // indexes of rings which should be "thick" (starting from 1)

  // draw x% of rings indexes that should be thick
  for (let i = 1; i <= centerEnd; i++) {
    drawPool.push(i);
  }

  let thickRingsPercentage = 0;
  switch (level) {
    case NOVICE:
      thickRingsPercentage = 0.1;
      break;
    case CONNOISSEUR:
      thickRingsPercentage = 0.3;
      break;
    case EXPERT:
      thickRingsPercentage = 0.5;
      break;
    default:
  }

  let thickRingsNo = questionsCount * thickRingsPercentage;
  if (thickRingsNo < centerEnd) {
    for (let i = 1; i <= thickRingsNo; i++) {
      const rand = random.element(drawPool);
      drawThick.push(rand);
      drawPool = drawPool.filter((e) => e !== rand);
    }
  } else {
    drawThick = [...drawPool];
  }

  for (let i = 1; i <= questionsCount; i++) {
    // ring belongs to the "center of woodcut"
    if (drawThick.includes(i)) {
      thickness.push(random.int(3, 4));
    } else {
      // draw from "thin"
      thickness.push(random.int(1, 2));
    }
  }

  //console.log('thickness', drawThick, thickness);

  return thickness;
}

/*
Randomized axis numbers with R and G initialized for each group 
R = 3, G = 4 
R = 6, G = 3 
R = 4, G = 5 
A[] = {1, 2, 3, 7, 8, 9, 10, 11, 12, 15, 16, 17 }
First element of axis numbers has always value of 1 
R can have value between 2 and 7 
G can have value between 3 and 5
*/

function getAxisNumbers(questionsCount) {
  const axisNumbers = [1];
  while (axisNumbers.length < questionsCount) {
    const R = random.int(2, 7);
    const G = random.int(3, 5);
    let lastValue = axisNumbers[axisNumbers.length - 1];
    for (let i = 1; i < R; i++) {
      lastValue++;
      axisNumbers.push(lastValue);
    }
    lastValue += G;
    axisNumbers.push(lastValue);
  }
  //console.log(axisNumbers);
  return axisNumbers;
}

/*
  Return top 3 cardinalities characteristics for certain consumer level
  decide which level of desciption should be chosen (from mock/characteristics.js) based on count of cardinalities and CARDINALITY_LEVELS
*/
function getTopCardinalities(cardinalitiesCount) {
  const count = [...cardinalitiesCount];
  const topCardinalities = [];
  for (let i = 1; i <= 3; i++) {
    const maxValue = Math.max(...count);
    const indexOfMaxValue = count.indexOf(maxValue);
    if (maxValue === 0) break;
    let level;
    if (maxValue >= CARDINALITY_LEVELS[indexOfMaxValue][1]) {
      level = "high";
    } else if (maxValue <= CARDINALITY_LEVELS[indexOfMaxValue][0]) {
      level = "low";
    } else {
      level = "med";
    }

    topCardinalities.push({
      cardinality: CARDINALITIES[indexOfMaxValue],
      level,
    });
    count[indexOfMaxValue] = -1;
  }

  // sort to have a level "high" first
  topCardinalities.sort((a, b) => {
    if (a.level === b.level) {
      return 0;
    }

    if (b.level === "high" || a.level === "low") {
      return 1;
    }

    if (a.level === "high" || b.level === "low") {
      return -1;
    }

    return 0;
  });
  return topCardinalities;
}

/*
  Return texture path:string for specific type:string['wood','bark','dirt', 'dirtBark'] and level:int[0-3]  
*/
function getTexturePath(level, type) {
  const textureLevelByType = TEXTURES[type][level];
  const randomFileName =
    textureLevelByType[random.int(0, textureLevelByType.length - 1)];

  return `/textures/${type}/${level}/${randomFileName}`;
}

export default calcVisualisationData;
